import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Nav from './components/common/Nav';
import Login from './pages/Login';
import Dashboard from './Dashboard';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import MentorManagement from './MentorManagement';
import MentorDashboard from './MentorDashboard';
import MentorStudents from './pages/MentorStudents';
import CallLogs from './pages/CallLogs';
import UploadStudents from './pages/UploadStudents';
import PrivateRoute from './components/common/PrivateRoute';
import RequireRole from './auth/RequireRole';
import './App.css';

const AppContent = ({ darkMode, onToggleTheme }) => {
  const { user, loading, role: userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  // Auto-redirect mentors away from admin pages
  useEffect(() => {
    if (!loading && user && userRole === 'mentor') {
      const currentPath = location.pathname;
      const adminPaths = ['/', '/dashboard', '/mentors', '/students', '/students/import'];
      if (adminPaths.includes(currentPath)) {
        navigate('/mentor', { replace: true });
      }
    }
  }, [user, userRole, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="app-loader">
        <div className="app-loader-dot"></div>
        <div className="app-loader-dot"></div>
        <div className="app-loader-dot"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors" style={{ background: 'var(--color-surface)', color: 'var(--color-text-primary)' }}>
      {user && <Nav darkMode={darkMode} onToggleTheme={onToggleTheme} />}
      {isLoginPage ? (
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                userRole === 'mentor' ? (
                  <Navigate to="/mentor" />
                ) : (
                  <Navigate to="/dashboard" />
                )
              ) : (
                <Login />
              )
            }
          />
        </Routes>
      ) : (
        <>
          <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
            <Routes>
              {/* Login route removed from here as it's handled above */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    {userRole === 'mentor' ? (
                      <Navigate to="/mentor" replace />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )}
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <RequireRole roles={['admin']}>
                    <Dashboard />
                  </RequireRole>
                }
              />
              <Route
                path="/students"
                element={
                  <RequireRole roles={['admin']}>
                    <Students />
                  </RequireRole>
                }
              />
              <Route
                path="/students/:id"
                element={
                  <RequireRole roles={['admin', 'mentor']}>
                    <StudentDetail />
                  </RequireRole>
                }
              />
              <Route
                path="/students/import"
                element={
                  <RequireRole roles={['admin']}>
                    <UploadStudents />
                  </RequireRole>
                }
              />
              <Route
                path="/mentors"
                element={
                  <RequireRole roles={['admin']}>
                    <MentorManagement />
                  </RequireRole>
                }
              />
              <Route
                path="/calls"
                element={
                  <PrivateRoute>
                    <CallLogs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mentor"
                element={
                  <RequireRole roles={['mentor']}>
                    <MentorDashboard />
                  </RequireRole>
                }
              />
              <Route
                path="/mentor/students"
                element={
                  <RequireRole roles={['mentor']}>
                    <MentorStudents />
                  </RequireRole>
                }
              />
            </Routes>
          </main>
          <footer className="max-w-6xl mx-auto px-4 py-6 text-xs text-slate-500 dark:text-slate-400">
            © {new Date().getFullYear()} Mentor Portal. All rights reserved.
          </footer>
        </>
      )}
    </div>
  );
};

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [darkMode]);

  const handleToggleTheme = () => setDarkMode((prev) => !prev);

  return (
    <Router>
      <AuthProvider>
        <AppContent darkMode={darkMode} onToggleTheme={handleToggleTheme} />
      </AuthProvider>
    </Router>
  );
}

export default App;
